// src/components/MinStayDefaultEditor.jsx — Phase 6.B PR #167c (ADR-101 D-5a)
//
// Composant éditeur min_stay_default réutilisable entre :
//   - Vue 1 fiche logement (SyncBeds24Tab → section "📐 Restrictions séjour")
//   - Vue 2 tableau Paramètres (Beds24SyncPanel → modal édition col 9)
//
// Q-UI-5 décision dev-senior : OPTION B (2 composants + hook partagé)
//   - useMinStayDefault hook gère fetch GET + save POST + refreshBeds24 GET
//   - MinStayDefaultEditor = bloc body réutilisable
//   - MinStayDefaultEditModal (autre fichier) = wrapper modal Esc/overlay
//
// Avantages OPTION B vs Option A (composant unique 3 variants) :
//   - Composant ≤ 200 lignes (MPRcv2-1)
//   - Hook réutilisable testable isolément
//   - Modal wrapper réutilise pattern standard (cf. PricingConfigEditModal)
//
// BPRcv2-1 fix : composant fait son propre fetch via useMinStayDefault, ignore
// toute prop `currentValue` issue de cfg V1 KV (toujours undefined).
//
// MPRcv2-3 fix : onSaved callback invalide 3 hooks via callback parent :
//   - Vue 1 : refetchCfg() + globalInvalidate() + usePricingConfigList.refetch()
//   - Vue 2 : usePricingConfigList.refetch() + globalInvalidate()
//   - (passé par parent via onSaved prop)

import { useState, useEffect, useRef } from 'react';
import { DS } from '../lib/ui';
import { useMinStayDefault } from '../lib/use-min-stay-default';

/**
 * Bloc body éditeur min_stay_default (réutilisable section + modal).
 *
 * Props :
 * @param {string} props.logementId
 * @param {'section'|'modal'} [props.mode='section']  - layout (cosmétique)
 * @param {() => void} [props.onSaved]  - callback après save success
 * @param {() => void} [props.onCancel] - callback annulation (modal seul)
 */
export default function MinStayDefaultEditor({
  logementId,
  mode = 'section',
  onSaved,
  onCancel,
}) {
  const {
    value,
    pricingMode,
    hasMapping,
    loading,
    error,
    driftDetected,
    beds24Raw,
    fetchedAt,
    beds24SyncStatus,  // PR #167c amend (§Auto-sync) — 'idle'|'syncing'|'synced'|'drift_corrected'|'no_op'|'failed'
    refetch,
    refreshBeds24,
    save,
  } = useMinStayDefault(logementId);

  // État local input + saving + toast inline
  const [inputValue, setInputValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null); // { kind, msg }

  // PR #167c amend C9 (NQAc2-1 fix) — ref input pour focus check anti-clobber.
  // Si l'admin est en train de taper dans l'input et que l'auto-sync découvre
  // un drift Beds24 ≠ DB qui modifie `value` (drift_corrected), on NE doit PAS
  // écraser silencieusement sa saisie. On skip donc la sync inputValue ↔ value
  // quand l'input a le focus (= user en cours d'édition). La sync reprend
  // normalement au blur (user a fini de taper).
  const inputRef = useRef(null);

  // Sync input ↔ value (post-fetch ou drift_corrected en arrière-plan).
  // Anti-clobber : skip si l'admin est en train de taper (input focused).
  useEffect(() => {
    if (loading) return;
    // NQAc2-1 : ne pas écraser la saisie utilisateur en cours.
    if (
      typeof document !== 'undefined' &&
      inputRef.current &&
      inputRef.current === document.activeElement
    ) {
      return;
    }
    setInputValue(value == null ? '' : String(value));
  }, [value, loading]);

  const readOnlyPricelabs = pricingMode === 'pricelabs';
  const disabledByNoMapping = !hasMapping;

  function flashToast(kind, msg, ms = 4000) {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), ms);
  }

  async function handleSave() {
    if (readOnlyPricelabs || disabledByNoMapping) return;

    // Parse + valider (NULL accepté pour clear)
    let parsed = null;
    if (inputValue !== '' && inputValue != null) {
      const n = Number(inputValue);
      if (!Number.isInteger(n) || n < 1 || n > 30) {
        flashToast('err', 'Doit être un entier entre 1 et 30.');
        return;
      }
      parsed = n;
    }

    setSaving(true);
    const result = await save(parsed);
    setSaving(false);
    if (!result.ok) {
      flashToast('err', `Erreur : ${result.error || 'inconnue'}`);
      return;
    }
    const label =
      parsed === null
        ? 'Min nuit défaut effacé (NULL).'
        : `Défaut min ${parsed} nuit${parsed > 1 ? 's' : ''} enregistré${result.dryRun ? ' (DRY_RUN)' : ' sur Beds24'}.`;
    flashToast('ok', label, 5000);
    onSaved?.();
  }

  async function handleRefresh() {
    await refreshBeds24();
    flashToast('info', 'Lecture Beds24 mise à jour.', 3000);
  }

  // Format date FR pour fetched_at
  function fmtAt(iso) {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch (_) {
      return iso;
    }
  }

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ fontSize: 12, color: '#888', padding: 8 }}>
        ⟳ Chargement de la config pricing…
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: '10px 12px',
          background: '#fdf2f2',
          color: DS.red,
          borderRadius: 8,
          fontSize: 12,
        }}
      >
        ⚠️ Erreur chargement : {error}{' '}
        <button
          onClick={refetch}
          style={{
            marginLeft: 8,
            background: 'transparent',
            border: `1px solid ${DS.red}`,
            color: DS.red,
            borderRadius: 4,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: 11,
          }}
        >
          Réessayer
        </button>
      </div>
    );
  }

  // Cas Logement non configuré (cfg absente OU mapping absent)
  if (disabledByNoMapping) {
    return (
      <div
        style={{
          padding: '10px 12px',
          background: '#fffbeb',
          color: '#92400e',
          borderRadius: 8,
          fontSize: 12,
          border: '1px solid #fde68a',
        }}
      >
        ⚠️ Logement non configuré pour push pricing — configurer d'abord dans
        Paramètres &gt; 🔌 Beds24.
      </div>
    );
  }

  return (
    <div style={{ fontFamily: DS.fontBody }}>
      {/* Toast inline */}
      {toast && (
        <div
          role="alert"
          style={{
            marginBottom: 10,
            padding: '8px 12px',
            borderRadius: 8,
            background:
              toast.kind === 'ok'
                ? '#edf7f0'
                : toast.kind === 'info'
                  ? '#eff6ff'
                  : '#fdf2f2',
            color:
              toast.kind === 'ok'
                ? DS.green
                : toast.kind === 'info'
                  ? DS.navy
                  : DS.red,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {toast.kind === 'ok' ? '✅' : toast.kind === 'info' ? 'ℹ️' : '⚠️'}{' '}
          {toast.msg}
        </div>
      )}

      {/* Cas TYPE A pricelabs : input read-only + mention */}
      {readOnlyPricelabs ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '8px 12px',
            background: '#eff6ff',
            borderRadius: 8,
            fontSize: 12,
            color: DS.navy,
          }}
        >
          <span style={{ fontWeight: 700 }}>
            Min nuit défaut : {value == null ? '—' : `${value} nuit${value > 1 ? 's' : ''}`}
          </span>
          <span
            style={{
              fontSize: 11,
              color: DS.skyDeep,
              fontWeight: 600,
            }}
          >
            🔵 Géré par PriceLabs (read-only)
          </span>
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap',
            }}
          >
            <label
              style={{
                fontSize: 11,
                color: '#888',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
              }}
            >
              Min nuits par défaut (1-30 ou vide pour NULL)
            </label>
            <input
              ref={inputRef}
              type="number"
              min={1}
              max={30}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              disabled={saving}
              style={{
                border: '1px solid #dde3ec',
                borderRadius: 7,
                padding: '6px 10px',
                fontSize: 13,
                width: 80,
                background: saving ? '#eef2f7' : '#f8fafd',
                fontFamily: DS.fontBody,
              }}
              placeholder="—"
              data-testid="min-stay-default-input"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              data-testid="min-stay-default-save"
              style={{
                background: DS.navy,
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                padding: '7px 14px',
                fontSize: 12,
                fontWeight: 700,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: DS.fontBody,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? '⟳ …' : '💾 Enregistrer'}
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={saving}
              style={{
                background: '#f1f5f9',
                color: DS.navy,
                border: '1px solid #dde3ec',
                borderRadius: 7,
                padding: '7px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: saving ? 'wait' : 'pointer',
                fontFamily: DS.fontBody,
              }}
              title="Lire valeur live Beds24 + détecter drift"
            >
              🔄 Recharger Beds24
            </button>
            {mode === 'modal' && onCancel && (
              <button
                type="button"
                onClick={onCancel}
                disabled={saving}
                style={{
                  background: 'transparent',
                  color: '#888',
                  border: '1px solid #dde3ec',
                  borderRadius: 7,
                  padding: '7px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: saving ? 'wait' : 'pointer',
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
            )}
          </div>

          {/* PR #167c amend (§Auto-sync) — indicateur sync compact en ligne.
              Subtil : ne bloque pas l'UX édition input. */}
          <SyncStatusIndicator
            beds24SyncStatus={beds24SyncStatus}
            fetchedAt={fetchedAt}
            fmtAt={fmtAt}
          />

          {/* Drift detected info (refreshBeds24 manuel) */}
          {fetchedAt && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
              Dernière lecture Beds24 : {fmtAt(fetchedAt)} · valeur brute Beds24 :{' '}
              <code>{beds24Raw == null ? 'null' : beds24Raw}</code>
              {driftDetected && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: '2px 8px',
                    borderRadius: 10,
                    background: '#fffbeb',
                    color: '#92400e',
                    border: '1px solid #fde68a',
                    fontWeight: 700,
                  }}
                >
                  ⚠️ Drift détecté
                </span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── PR #167c amend (§Auto-sync) — indicateur compact en ligne ────────────
//
// Affiche un petit pill texte selon `beds24SyncStatus` du hook
// `useMinStayDefault`. Subtil, non-bloquant. Pas de spinner lourd.
//
// États :
//   - 'idle' / 'no_op'  → rien affiché (silencieux)
//   - 'syncing'         → "⟳ Sync Beds24..."
//   - 'synced'          → "✅ Sync OK" + date
//   - 'drift_corrected' → "✅ Mis à jour depuis Beds24" + date
//   - 'failed'          → "⚠️ Sync Beds24 échouée — valeur cache DB"
function SyncStatusIndicator({ beds24SyncStatus, fetchedAt, fmtAt }) {
  if (!beds24SyncStatus || beds24SyncStatus === 'idle' || beds24SyncStatus === 'no_op') {
    return null;
  }

  const styles = {
    syncing: { color: '#475569', bg: '#f1f5f9', border: '1px solid #cbd5e1' },
    synced: { color: DS.green, bg: '#edf7f0', border: '1px solid #b6e3c4' },
    drift_corrected: { color: DS.green, bg: '#edf7f0', border: '1px solid #b6e3c4' },
    failed: { color: '#92400e', bg: '#fffbeb', border: '1px solid #fde68a' },
  };
  const labels = {
    syncing: '⟳ Sync Beds24…',
    synced: '✅ Sync OK',
    drift_corrected: '✅ Mis à jour depuis Beds24',
    failed: '⚠️ Sync Beds24 échouée — valeur cache DB',
  };
  const cfg = styles[beds24SyncStatus];
  const label = labels[beds24SyncStatus];
  if (!cfg || !label) return null;

  return (
    <div
      data-testid="min-stay-default-sync-status"
      data-sync-status={beds24SyncStatus}
      role="status"
      aria-live="polite"
      style={{
        marginTop: 8,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 10px',
        background: cfg.bg,
        color: cfg.color,
        border: cfg.border,
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {label}
      {fetchedAt && beds24SyncStatus !== 'syncing' && (
        <span style={{ marginLeft: 4, color: '#888', fontWeight: 500 }}>
          · {fmtAt(fetchedAt)}
        </span>
      )}
    </div>
  );
}
